/**
 * Shared constants and style objects for the retargeting feature.
 * Reduces duplication across step components.
 */

import { RETARGETING_STATUS_LABELS } from '@/shared/constants';

import { RailsConfig } from './types';

// Field and Section Labels
export const FIELD_LABEL_SX = {
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: '#111',
  mb: '5px',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontFamily: 'inherit',
};

/** Red asterisk marking a required field. */
export const REQUIRED_SX = {
  color: '#dc2626',
  fontWeight: 700,
  ml: '-2px',
  fontFamily: 'inherit',
};

export const SECTION_LABEL_SX = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.07em',
  color: '#111',
  mb: 1.25,
  mt: 2.5,
  fontFamily: 'inherit',
};

export const SECTION_LABEL_LG_SX = {
  fontSize: 15,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.07em',
  color: '#000000ff',
  mb: 1.25,
  mt: 2.5,
  fontFamily: 'inherit',
};

// Input Fields
export const INPUT_LG_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    height: 44,
    '& fieldset': { borderColor: '#e4e4e4' },
    '&:hover fieldset': { borderColor: '#c0cfe8' },
    '&.Mui-focused fieldset': { borderColor: '#1c9dea' },
  },
  '& .MuiInputBase-input': {
    fontSize: 13,
    color: '#1a1a1a',
    py: '0px',
    fontFamily: 'inherit',
  },
};

/**
 * DatePicker equivalent of INPUT_LG_SX. MUI X v8 fields render their own
 * `.MuiPickersOutlinedInput-root` + section list instead of
 * `.MuiOutlinedInput-root` + `<input>`, so INPUT_LG_SX alone leaves the picker
 * at its default ~56px height next to 44px text fields. Both class sets are
 * targeted so this stays correct either way.
 */
export const DATE_INPUT_SX = {
  width: '100%',
  '& .MuiPickersOutlinedInput-root, & .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    height: 44,
    fontSize: 13,
    fontFamily: 'inherit',
    color: '#1a1a1a',
    '& fieldset': { borderColor: '#e4e4e4' },
    '&:hover fieldset': { borderColor: '#c0cfe8' },
    '&.Mui-focused fieldset': { borderColor: '#1c9dea' },
  },
  '& .MuiPickersSectionList-root, & .MuiInputBase-input': {
    py: 0,
    fontSize: 13,
    fontFamily: 'inherit',
  },
  '& .MuiPickersInputBase-sectionsContainer': { py: 0 },
  '& .MuiIconButton-root': { p: '6px', mr: '-4px' },
  '& .MuiIconButton-root .MuiSvgIcon-root': { fontSize: 18, color: '#66788a' },
};

// Step 2: Sample CSV templates
/** Downloadable template for the site list. Headers are what the parser expects. */
export const SITE_CSV_SAMPLE = [
  'lat,lng',
  '19.0760,72.8777',
  '28.6139,77.2090',
  '12.9716,77.5946',
].join('\n');

export const SITE_CSV_FILENAME = 'sample-site-list.csv';

/** Downloadable template for the optional MAID list - single `Ma id` column. */
export const MAID_CSV_SAMPLE = [
  'Ma id',
  '38400000-8cf0-11bd-b23e-10b96e40000d',
  '6b8e4f1a-3c2d-4e5f-9a7b-1c2d3e4f5a6b',
  'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
].join('\n');

export const MAID_CSV_FILENAME = 'sample-maid-list.csv';

// Reach Model Shares (mock shares used to derive the live estimate)
/**
 * Identity graph size the per-market audience is carved from. Sized so a
 * single market reads in lakhs (42M × 4.5% ≈ 18.9L per market), which is the
 * scale a retargeting pool actually lands at - not crores.
 */
export const GRAPH_BASE_REACH = 42_000_000;
export const CITY_SHARE = 0.045;

export const AGE_SHARES: Record<string, number> = {
  '18-21': 0.13,
  '22-35': 0.34,
  '36-45': 0.24,
  '46-55': 0.17,
  '55+': 0.12,
};

export const DEVICE_SHARES: Record<string, number> = {
  Mobile: 0.78,
  Desktop: 0.35,
  CTV: 0.12,
};

/** Shares sum to 1, so "all selected" is the same as no OS filter. */
export const OS_SHARES: Record<string, number> = {
  Android: 0.52,
  iOS: 0.17,
  Coolida: 0.16,
  'Cloud TV': 0.1,
  Linux: 0.05,
};

/**
 * Owned-channel activation - parked until the messaging integrations land, so
 * the chips render disabled.
 */
export const OWNED_CHANNELS = ['Email', 'Push', 'WhatsApp', 'SMS'];

export const TIER_SHARES: Record<string, number> = {
  Budget: 0.4,
  'Mid-range': 0.42,
  Flagship: 0.18,
};

// Audience Filter Options
// Chip options are derived from the share maps above, so adding a bucket to a
// share map can't leave the filter panel out of sync with the reach model.
export const GENDER_OPTIONS = ['all', 'female', 'male'];
export const AGE_OPTIONS = Object.keys(AGE_SHARES);
export const DEVICE_OPTIONS = Object.keys(DEVICE_SHARES);
export const OS_OPTIONS = Object.keys(OS_SHARES);
export const DEVICE_TIER_OPTIONS = Object.keys(TIER_SHARES);

/**
 * Affinity signals per interest category. The Affinity chips are the union of
 * the signals for whatever interests are selected, so picking Fashion +
 * Finance offers both sets - an affinity list that can't drift from the
 * interests it belongs to.
 */
export const INTEREST_AFFINITIES: Record<string, string[]> = {
  Fashion: [
    'Fashion app active (7d)',
    'New season browser',
    'Wishlist adder',
    'Cart abandoner',
  ],
  Beauty: ['Skincare researcher', 'Salon booker', 'Beauty box subscriber'],
  Lifestyle: ['Home decor browser', 'Premium shopper', 'Weekend planner'],
  Travel: [
    'Flight searcher (30d)',
    'Hotel comparer',
    'Frequent flyer',
    'Visa / forex intent',
  ],
  Finance: [
    'Credit card intent',
    'Insurance comparer',
    'Mutual fund investor',
    'Loan enquiry (30d)',
  ],
  Sports: ['Live match streamer', 'Fantasy league player', 'Sportswear buyer'],
  'Food & Dining': [
    'Food delivery frequent',
    'Fine dining reserver',
    'Quick-commerce grocery',
  ],
  Tech: ['Gadget upgrade cycle', 'Electronics comparer', 'Early adopter'],
  Parenting: ['Baby care buyer', 'School / edtech intent', 'Family planner'],
  'Shopping Intent': [
    'Cart abandoner',
    'Sale hunter',
    'Recent purchase (30d)',
    'Price comparer',
  ],
  Fitness: ['Gym membership intent', 'Wearable owner', 'Supplement buyer'],
  Entertainment: ['OTT subscriber', 'Cinema ticket buyer', 'Music streamer'],
};

export const INTEREST_CATEGORIES = Object.keys(INTEREST_AFFINITIES);

/** Signals offered for the given interests - union, de-duplicated, stable order. */
export const affinitiesForInterests = (interests: string[]): string[] => [
  ...new Set(interests.flatMap(i => INTEREST_AFFINITIES[i] ?? [])),
];

export const EXCLUSION_ITEMS = [
  {
    key: 'recent_converters',
    title: 'Recent converters',
    description: '30 days',
  },
  {
    key: 'high_frequency',
    title: 'High-frequency exposed',
    description: '5+ ads',
  },
  {
    key: 'loyalty_crm',
    title: 'Loyalty / CRM members',
    description: 'Active members',
  },
  {
    key: 'competitor_engagers',
    title: 'Competitor brand engagers',
    description: 'Past 90d',
  },
];

// Wizard Config
export const WIZARD_STEPS = [
  'Campaign & Audience',
  'Select the Site',
  'Audience Builder',
  'Reach & Budget',
  'Rails Config',
  'Creatives',
  'Submitted',
];

export const PROMPT_CHIPS = [
  'Retargeting past campaign',
  'Brand awareness - all channels',
  'Retarget from DOOH',
  'Performance, mobile-first',
  'Festive Push',
];

/** One is picked at random each time the prompt screen mounts. */
export const WELCOME_LINES = [
  'How can I help you today?',
  'Hey there — what are we launching today?',
  'Good to see you. What’s the campaign?',
  'Let’s build your next campaign.',
  'Tell me about your campaign — I’ll take it from there.',
];

/**
 * Channels start unallocated - budget and share are the planner's call, not a
 * prefilled guess. `reach` is the estimated monthly impression pool per
 * channel, which isn't user input.
 */
export const DEFAULT_CHANNELS = [
  // DOOH is the audience *source*, not a delivery channel, so its 5.8L was
  // spread across the three delivery channels in proportion to their own
  // reach (×1.18) - dropping it outright would have lost 15% of the pool.
  {
    id: 1,
    name: 'Web',
    enabled: true,
    reach: 21.4,
    budget: 0,
    percent: 0,
    locked: false,
  },
  {
    id: 2,
    name: 'Mobile / App',
    enabled: true,
    reach: 14.8,
    budget: 0,
    percent: 0,
    locked: false,
  },
  {
    id: 3,
    name: 'CTV',
    enabled: true,
    reach: 2.1,
    budget: 0,
    percent: 0,
    locked: false,
  },
];

export const DEFAULT_RAILS: RailsConfig[] = [
  {
    id: 'default-1',
    name: 'Standard publisher rules',
    description: 'Publisher allowlists, blocklists and placement constraints.',
    appliesTo: ['Web', 'Mobile/App', 'CTV'],
    webRules: {
      mode: 'allowlist' as const,
      include: ['News', 'Finance', 'Lifestyle'],
      exclude: [],
    },
    mobileRules: {
      mode: 'allowlist' as const,
      include: ['Myntra', 'AJIO', 'Nykaa'],
      exclude: ['Gaming', 'Adult'],
    },
    ctvRules: {
      platforms: ['Sony LIV', 'JioHotstar', 'ZEE5'],
      genres: ['Sports', 'Entertainment'],
      languages: ['English', 'Hindi'],
    },
  },
  {
    id: 'default-2',
    name: 'CTV Rules',
    description: 'Premium large-screen inventory only.',
    appliesTo: ['CTV'],
    webRules: { mode: 'allowlist' as const, include: [], exclude: [] },
    mobileRules: { mode: 'allowlist' as const, include: [], exclude: [] },
    ctvRules: {
      platforms: ['Sony LIV', 'JioHotstar'],
      genres: ['Sports', 'Entertainment', 'News'],
      languages: ['English', 'Hindi'],
    },
  },
];

/**
 * Rails channel id → the channel name used in the Step 4 allocation table.
 * The two lists spell Mobile differently, so every cross-step lookup goes
 * through this map rather than re-deriving the mapping locally.
 */
export const RAILS_TO_CHANNEL: Record<string, string> = {
  Web: 'Web',
  'Mobile/App': 'Mobile / App',
  CTV: 'CTV',
};

// Channel Formats
/**
 * Accepted creative slots per channel. Each entry is its own upload slot in
 * Step 6 - an ad server rejects a 728×90 dropped into a 300×250 placement, so
 * the sizes can't share one bucket.
 */
export const CHANNEL_FORMATS: Record<string, string[]> = {
  Web: ['300×250', '728×90', '160×600'],
  'Mobile / App': ['320×50', '300×250', 'Interstitial 320×480'],
  CTV: ['VAST 4.0 · 15s', 'VAST 4.0 · 30s'],
};

// Campaign List
/** Keys are the API's status enum, so they go straight into the list filter. */
export const CAMPAIGN_LIST_FILTERS = [
  {
    key: 'status',
    label: 'Status',
    type: 'terms',
    values: Object.entries(RETARGETING_STATUS_LABELS).map(([key, label]) => ({
      key,
      label,
    })),
  },
];

/** The label the table and detail header show for an API status. */
export const normalizeRetargetingStatus = (statusValue: unknown) =>
  RETARGETING_STATUS_LABELS[String(statusValue || '').toUpperCase()] ??
  RETARGETING_STATUS_LABELS.DRAFT;
